import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Side from "./Side";
import {
  Card, Row, Col, Table, Badge, Form, Spinner, Alert, Modal, Pagination
} from "react-bootstrap";

const BASE = "https://dailydish.in";

const StatCard = ({ label, value, sub, color = "#1e4a1e" }) => (
  <Card style={{ border: `2px solid ${color}20`, borderRadius: 14, height: "100%" }}>
    <Card.Body style={{ padding: "16px 20px" }}>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>{sub}</div>}
    </Card.Body>
  </Card>
);

const STATUS_COLORS = {
  Confirmed: "success",
  Delivered: "success",
  Cancelled: "danger",
  Skipped: "secondary",
  "Pending Payment": "warning",
  Cooking: "info",
  Packing: "info",
  Packed: "info",
  "On the way": "primary",
  ontheway: "primary",
  Undelivered: "danger",
  "Sourced Fresh": "info",
};

const UserOrdersModal = ({ user, onClose }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const LIMIT = 10;

  const fetchOrders = useCallback(async (pg) => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${BASE}/api/Hub/users/${user.customerId}/orders?page=${pg}&limit=${LIMIT}`
      );
      setOrders(res.data.orders);
      setPagination(res.data.pagination);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    setPage(1);
    fetchOrders(1);
  }, [fetchOrders]);

  const handlePageChange = (pg) => {
    setPage(pg);
    fetchOrders(pg);
  };

  const buildPaginationItems = () => {
    const items = [];
    const { totalPages } = pagination;
    const range = 2;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || (i >= page - range && i <= page + range)) {
        items.push(
          <Pagination.Item key={i} active={i === page} onClick={() => handlePageChange(i)}>
            {i}
          </Pagination.Item>
        );
      } else if (
        (i === page - range - 1 || i === page + range + 1) &&
        items[items.length - 1]?.type !== Pagination.Ellipsis
      ) {
        items.push(<Pagination.Ellipsis key={`e${i}`} disabled />);
      }
    }
    return items;
  };

  return (
    <Modal show={!!user} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton style={{ background: "#1e4a1e", color: "#fff  !important", borderRadius: "12px 12px 0 0" }}>
        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
          📦 Orders — {user?.name} ({user?.phone})
          <span style={{ fontSize: 12, fontWeight: 400, marginLeft: 12, opacity: 0.8 }}>
            {pagination.total} total orders
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "20px", maxHeight: "70vh", overflowY: "auto" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spinner animation="border" style={{ color: "#1e4a1e" }} />
          </div>
        ) : orders.length === 0 ? (
          <div style={{ textAlign: "center", color: "#aaa", padding: 40 }}>No orders found</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <Table hover style={{ fontSize: 13 }}>
              <thead style={{ background: "#f0f7ee" }}>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Session</th>
                  <th>Hub</th>
                  <th>Items</th>
                  <th>Order Total (₹)</th>
                  <th>Offer Disc (₹)</th>
                  <th>Wallet (₹)</th>
                  <th>Paid (₹)</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o, i) => (
                  <tr key={o._id}>
                    <td style={{ color: "#aaa" }}>{(page - 1) * LIMIT + i + 1}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {new Date(o.deliveryDate).toLocaleDateString("en-IN", {
                        day: "numeric", month: "short", year: "numeric"
                      })}
                    </td>
                    <td>
                      <Badge
                        bg={o.session?.toLowerCase() === "lunch" ? "warning" : o.session?.toLowerCase() === "breakfast" ? "info" : "secondary"}
                        style={{ color: "#333", fontSize: 11 }}
                      >
                        {o.session}
                      </Badge>
                    </td>
                    <td style={{ fontSize: 12 }}>{o.hubName || "—"}</td>
                    <td style={{ fontSize: 12, maxWidth: 200 }}>
                      {o.products?.map(p => `${p.foodName}${p.quantity > 1 ? ` ×${p.quantity}` : ""}`).join(", ") || "—"}
                    </td>
                    <td>₹{o.slotTotalAmount ?? "—"}</td>
                    <td style={{ color: o.acquisitionDiscount > 0 ? "#2e7d32" : "#aaa", fontWeight: o.acquisitionDiscount > 0 ? 600 : 400 }}>
                      {o.acquisitionDiscount > 0 ? `-₹${o.acquisitionDiscount}` : "—"}
                    </td>
                    <td style={{ color: o.discountWallet > 0 ? "#1565c0" : "#aaa" }}>
                      {o.discountWallet > 0 ? `-₹${o.discountWallet}` : "—"}
                    </td>
                    <td style={{ fontWeight: 600 }}>₹{o.payableAmount ?? "—"}</td>
                    <td>
                      <Badge bg={STATUS_COLORS[o.status] || "secondary"} style={{ fontSize: 11 }}>
                        {o.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>
      {pagination.totalPages > 1 && (
        <Modal.Footer style={{ justifyContent: "center" }}>
          <Pagination size="sm" className="mb-0">
            <Pagination.Prev disabled={page === 1} onClick={() => handlePageChange(page - 1)} />
            {buildPaginationItems()}
            <Pagination.Next disabled={page === pagination.totalPages} onClick={() => handlePageChange(page + 1)} />
          </Pagination>
        </Modal.Footer>
      )}
    </Modal>
  );
};

const HubOfferCampaign = () => {
  const [hubs, setHubs] = useState([]);
  const [selectedHubId, setSelectedHubId] = useState("");
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hubsLoading, setHubsLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  // Load hubs with active offers
  useEffect(() => {
    axios.get(`${BASE}/api/Hub/hubs`)
      .then(res => {
        const withOffer = (Array.isArray(res.data) ? res.data : [])
          .filter(h => h.hubOffer);
        setHubs(withOffer);
        if (withOffer.length > 0) setSelectedHubId(withOffer[0].hubId);
      })
      .catch(() => setError("Failed to load hubs"))
      .finally(() => setHubsLoading(false));
  }, []);

  const fetchCampaign = useCallback(async (hubId) => {
    if (!hubId) return;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${BASE}/api/Hub/hubs/${hubId}/offer/campaign`);
      setCampaign(res.data);
    } catch {
      setError("Failed to load campaign data");
      setCampaign(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedHubId) fetchCampaign(selectedHubId);
  }, [selectedHubId, fetchCampaign]);

  const filteredUsers = campaign?.users?.filter(u => {
    const q = search.toLowerCase();
    return (
      u.name?.toLowerCase().includes(q) ||
      u.phone?.includes(q)
    );
  }) ?? [];

  const fmt = (n) => n !== undefined && n !== null ? n : "—";
  const pct = (n, t) => t > 0 ? `${Math.round((n / t) * 100)}%` : "0%";

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f5f5f5" }}>
      {/* <Side /> */}
      <div style={{ flex: 1, padding: "24px", overflowX: "auto" }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontWeight: 800, color: "#1e4a1e", margin: 0 }}>
            🎁 Hub Offer Campaign Dashboard
          </h2>
          <p style={{ color: "#888", margin: "4px 0 0" }}>
            Acquisition offer analytics — registered users, meals used, spending
          </p>
        </div>

        {/* Hub selector */}
        <Card style={{ borderRadius: 14, marginBottom: 24 }}>
          <Card.Body style={{ padding: "16px 20px" }}>
            <Form.Group style={{ maxWidth: 380 }}>
              <Form.Label style={{ fontWeight: 600, fontSize: 13 }}>Select Hub</Form.Label>
              {hubsLoading ? (
                <Spinner size="sm" animation="border" />
              ) : (
                <Form.Select
                  value={selectedHubId}
                  onChange={e => setSelectedHubId(e.target.value)}
                  style={{ borderRadius: 10 }}
                >
                  {hubs.map(h => (
                    <option key={h.hubId} value={h.hubId}>{h.hubName}</option>
                  ))}
                  {hubs.length === 0 && (
                    <option value="">No hubs with active offers</option>
                  )}
                </Form.Select>
              )}
            </Form.Group>
          </Card.Body>
        </Card>

        {error && <Alert variant="danger">{error}</Alert>}

        {loading && (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spinner animation="border" style={{ color: "#1e4a1e" }} />
            <p style={{ marginTop: 12, color: "#888" }}>Loading campaign data…</p>
          </div>
        )}

        {!loading && campaign && campaign.hasOffer && (
          <>
            {/* Offer config strip */}
            <Card style={{ borderRadius: 14, marginBottom: 24, background: "#1e4a1e", border: "none" }}>
              <Card.Body style={{ padding: "14px 20px", display: "flex", flexWrap: "wrap", gap: 24 }}>
                {[
                  ["User Cap", campaign.offerConfig.userCap],
                  ["Offer Cap", `₹${campaign.offerConfig.offerCap}/day`],
                  ["Validity", `${campaign.offerConfig.validity} days`],
                  ["Meals/User", campaign.offerConfig.mealsPerValidity],
                  ["Status", campaign.offerConfig.isActive ? "Active" : "Closed"],
                ].map(([k, v]) => (
                  <div key={k} style={{ color: "#fff" }}>
                    <div style={{ fontSize: 11, opacity: 0.7 }}>{k}</div>
                    <div style={{ fontSize: 16, fontWeight: 700 }}>{v}</div>
                  </div>
                ))}
              </Card.Body>
            </Card>

            {/* Hub Revenue & Session Stats */}
            {(() => {
              const hs = campaign.hubStats || { totalOrders: 0, totalRevenue: 0, totalDiscountGiven: 0, sessionBreakdown: { breakfast: 0, lunch: 0, dinner: 0 } };
              return (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#555", marginBottom: 10, marginTop: 4 }}>
                    📊 Hub-Wide Stats (All Orders from this Hub)
                  </div>
                  <Row className="g-3 mb-4">
                    <Col xs={6} md={3}>
                      <StatCard
                        label="Total Orders"
                        value={fmt(hs.totalOrders)}
                        sub="Confirmed + Delivered"
                        color="#1e4a1e"
                      />
                    </Col>
                    <Col xs={6} md={3}>
                      <StatCard
                        label="Total Revenue Received"
                        value={`₹${fmt(hs.totalRevenue)}`}
                        sub="Sum of all payable amounts"
                        color="#1b5e20"
                      />
                    </Col>
                    <Col xs={6} md={3}>
                      <StatCard
                        label="Total Offer Discount"
                        value={`₹${fmt(hs.totalDiscountGiven)}`}
                        sub="Acquisition discounts applied"
                        color="#b71c1c"
                      />
                    </Col>
                    <Col xs={6} md={3}>
                      <StatCard
                        label="Gross Order Value"
                        value={`₹${fmt(Math.round(((hs.totalRevenue || 0) + (hs.totalDiscountGiven || 0)) * 100) / 100)}`}
                        sub="Revenue + discounts given"
                        color="#4a148c"
                      />
                    </Col>
                    <Col xs={4} md={2}>
                      <Card style={{ border: "2px solid #f57f1730", borderRadius: 14, height: "100%", background: "#fff8e1" }}>
                        <Card.Body style={{ padding: "16px 20px" }}>
                          <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>☀️ Breakfast</div>
                          <div style={{ fontSize: 26, fontWeight: 800, color: "#f57f17" }}>{fmt(hs.sessionBreakdown?.breakfast)}</div>
                          <div style={{ fontSize: 11, color: "#aaa" }}>orders</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col xs={4} md={2}>
                      <Card style={{ border: "2px solid #1565c030", borderRadius: 14, height: "100%", background: "#e3f2fd" }}>
                        <Card.Body style={{ padding: "16px 20px" }}>
                          <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>🍱 Lunch</div>
                          <div style={{ fontSize: 26, fontWeight: 800, color: "#1565c0" }}>{fmt(hs.sessionBreakdown?.lunch)}</div>
                          <div style={{ fontSize: 11, color: "#aaa" }}>orders</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col xs={4} md={2}>
                      <Card style={{ border: "2px solid #880e4f30", borderRadius: 14, height: "100%", background: "#fce4ec" }}>
                        <Card.Body style={{ padding: "16px 20px" }}>
                          <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>🌙 Dinner</div>
                          <div style={{ fontSize: 26, fontWeight: 800, color: "#880e4f" }}>{fmt(hs.sessionBreakdown?.dinner)}</div>
                          <div style={{ fontSize: 11, color: "#aaa" }}>orders</div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </>
              );
            })()}

            {/* Summary cards */}
            <div style={{ fontSize: 13, fontWeight: 700, color: "#555", marginBottom: 10 }}>
              🎁 Offer Campaign Summary
            </div>
            <Row className="g-3 mb-4">
              <Col xs={6} md={3}>
                <StatCard
                  label="Total Registered"
                  value={fmt(campaign.summary.totalRegistered)}
                  sub={`${fmt(campaign.summary.withinCap)} within cap · ${fmt(campaign.summary.afterCap)} after cap`}
                />
              </Col>
              <Col xs={6} md={3}>
                <StatCard
                  label="Total Meals Used"
                  value={fmt(campaign.summary.totalMealsUsed)}
                  sub={`Avg ${fmt(campaign.summary.avgMealsPerUser)} per user`}
                  color="#2e7d32"
                />
              </Col>
              <Col xs={6} md={3}>
                <StatCard
                  label="Users Used ≥1 Meal"
                  value={fmt(campaign.summary.usersWhoUsedAtLeastOne)}
                  sub={pct(campaign.summary.usersWhoUsedAtLeastOne, campaign.summary.withinCap) + " of registered"}
                  color="#1565c0"
                />
              </Col>
              <Col xs={6} md={3}>
                <StatCard
                  label="Completed All Meals"
                  value={fmt(campaign.summary.usersWhoUsedAll)}
                  sub="Used every allocated meal"
                  color="#6a1b9a"
                />
              </Col>
              <Col xs={6} md={3}>
                <StatCard
                  label="Active Users"
                  value={fmt(campaign.summary.activeUsers)}
                  sub="Offer still valid"
                  color="#00838f"
                />
              </Col>
              <Col xs={6} md={3}>
                <StatCard
                  label="Repeat Usage"
                  value={`${fmt(campaign.summary.repetitionRate)}%`}
                  sub="Used more than once"
                  color="#e65100"
                />
              </Col>
              <Col xs={6} md={3}>
                <StatCard
                  label="Total Discount Given"
                  value={`₹${fmt(campaign.summary.totalDiscountGiven)}`}
                  sub="Across all users"
                  color="#b71c1c"
                />
              </Col>
            </Row>

            {/* User table */}
            <Card style={{ borderRadius: 14 }}>
              <Card.Body style={{ padding: "20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
                  <h5 style={{ fontWeight: 700, margin: 0 }}>User Details</h5>
                  <Form.Control
                    type="text"
                    placeholder="Search name or phone…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ maxWidth: 260, borderRadius: 10 }}
                  />
                </div>

                <div style={{ overflowX: "auto" }}>
                  <Table hover style={{ fontSize: 13 }}>
                    <thead style={{ background: "#f0f7ee" }}>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Phone</th>
                        <th>Meals Used / Allocated</th>
                        <th>Total Orders</th>
                        <th>Avg Order Value (₹)</th>
                        <th>Total Discount (₹)</th>
                        <th>Total Paid (₹)</th>
                        <th>Status</th>
                        <th>Last Used</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan={10} style={{ textAlign: "center", color: "#aaa", padding: 32 }}>
                            No users found
                          </td>
                        </tr>
                      ) : filteredUsers.map((u, i) => (
                        <tr
                          key={u.customerId}
                          onClick={() => setSelectedUser(u)}
                          style={{ cursor: "pointer" }}
                        >
                          <td style={{ color: "#aaa" }}>{i + 1}</td>
                          <td style={{ fontWeight: 600, color: "#1e4a1e", textDecoration: "underline dotted" }}>{u.name || "—"}</td>
                          <td>{u.phone}</td>
                          <td>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <div
                                style={{
                                  background: "#e8f5e9",
                                  borderRadius: 99,
                                  height: 6,
                                  flex: 1,
                                  overflow: "hidden",
                                  minWidth: 60,
                                }}
                              >
                                <div
                                  style={{
                                    background: u.usedAllMeals ? "#2e7d32" : "#66bb6a",
                                    width: `${Math.min(100, (u.mealsUsed / u.mealsAllocated) * 100)}%`,
                                    height: "100%",
                                    borderRadius: 99,
                                    transition: "width 0.3s",
                                  }}
                                />
                              </div>
                              <span style={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                                {u.mealsUsed} / {u.mealsAllocated}
                              </span>
                            </div>
                          </td>
                          <td>{u.totalOrders ?? u.ordersWithOffer}</td>
                          <td>{u.avgOrderValue > 0 ? u.avgOrderValue : "—"}</td>
                          <td style={{ color: "#b71c1c", fontWeight: 600 }}>
                            {u.totalDiscountGiven > 0 ? `₹${u.totalDiscountGiven}` : "—"}
                          </td>
                          <td>{u.totalAmountPaid > 0 ? `₹${u.totalAmountPaid}` : "—"}</td>
                          <td>
                            {u.usedAllMeals ? (
                              <Badge bg="success">Completed</Badge>
                            ) : u.isActive ? (
                              <Badge bg="primary">Active</Badge>
                            ) : (
                              <Badge bg="secondary">Expired</Badge>
                            )}
                          </td>
                          <td style={{ color: "#888", fontSize: 12 }}>
                            {u.lastUsedAt
                              ? new Date(u.lastUsedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                              : "Never"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div style={{ marginTop: 12, fontSize: 12, color: "#aaa" }}>
                  Showing {filteredUsers.length} of {campaign.users?.length ?? 0} users
                </div>
              </Card.Body>
            </Card>
          </>
        )}

        {!loading && campaign && !campaign.hasOffer && (
          <Alert variant="warning">No offer configured for this hub.</Alert>
        )}
      </div>

      <UserOrdersModal user={selectedUser} onClose={() => setSelectedUser(null)} />
    </div>
  );
};

export default HubOfferCampaign;
